import {
  BookmarkPlus,
  BookOpenCheck,
  ExternalLink,
  Plus,
  Search,
  Star,
  Trash2,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useI18n } from './I18nContext';
import { TEAM_TOPICS, PLAYER_TRAINING } from './trainingLibrary';

type SessionItem = {
  id: string;
  title: string;
  category: string;
  level: string;
  duration: number;
  objective: string;
  sourceUrl: string;
  favourite: boolean;
  createdAt: string;
};

const LIBRARY_KEY = 'ecc_session_library_v1';

function starterSessions(): SessionItem[] {
  const player = Object.entries(PLAYER_TRAINING).slice(0, 5).map(([id, item], index) => ({
    id: `starter-player-${id}`,
    title: item.label,
    category: 'Individual',
    level: 'Senior',
    duration: 20 + index * 5,
    objective: item.organisation,
    sourceUrl: item.sourceUrl,
    favourite: index < 2,
    createdAt: new Date().toISOString(),
  }));
  const team = TEAM_TOPICS.slice(0, 5).map((item, index) => ({
    id: `starter-team-${item.id}`,
    title: item.label,
    category: item.domain === 'set_piece' ? 'Set Pieces' : 'Team Tactical',
    level: 'Senior',
    duration: 30 + index * 5,
    objective: item.objective,
    sourceUrl: item.sourceUrl,
    favourite: false,
    createdAt: new Date().toISOString(),
  }));
  return [...player, ...team];
}

function loadLibrary() {
  try {
    const raw = localStorage.getItem(LIBRARY_KEY);
    if (!raw) return starterSessions();
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed as SessionItem[] : starterSessions();
  } catch {
    return starterSessions();
  }
}

export function SessionLibrary() {
  const { t } = useI18n();
  const [sessions, setSessions] = useState<SessionItem[]>(loadLibrary);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [showCreate, setShowCreate] = useState(false);
  const [draft, setDraft] = useState({
    title: '',
    category: 'Team Tactical',
    level: 'Senior',
    duration: 45,
    objective: '',
    sourceUrl: '',
  });

  useEffect(() => {
    try {
      localStorage.setItem(LIBRARY_KEY, JSON.stringify(sessions));
    } catch {
      // Device-local optional storage only.
    }
  }, [sessions]);

  const categories = useMemo(
    () => ['All', ...Array.from(new Set(sessions.map((item) => item.category)))],
    [sessions],
  );

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return sessions
      .filter((item) => category === 'All' || item.category === category)
      .filter((item) =>
        !query ||
        item.title.toLowerCase().includes(query) ||
        item.objective.toLowerCase().includes(query) ||
        item.category.toLowerCase().includes(query),
      )
      .sort((a, b) => Number(b.favourite) - Number(a.favourite));
  }, [sessions, search, category]);

  function createSession() {
    if (!draft.title.trim()) return;
    setSessions((current) => [
      {
        id: crypto.randomUUID(),
        title: draft.title.trim(),
        category: draft.category,
        level: draft.level,
        duration: Math.max(5, Number(draft.duration) || 45),
        objective: draft.objective.trim(),
        sourceUrl: draft.sourceUrl.trim(),
        favourite: false,
        createdAt: new Date().toISOString(),
      },
      ...current,
    ]);
    setDraft({
      title: '',
      category: 'Team Tactical',
      level: 'Senior',
      duration: 45,
      objective: '',
      sourceUrl: '',
    });
    setShowCreate(false);
  }

  function toggleFavourite(id: string) {
    setSessions((current) =>
      current.map((item) => item.id === id ? { ...item, favourite: !item.favourite } : item),
    );
  }

  function removeSession(id: string) {
    setSessions((current) => current.filter((item) => item.id !== id));
  }

  return (
    <section className="metal-card session-library">
      <div className="section-title-line">
        <div>
          <p className="eyebrow">SESSION LIBRARY</p>
          <h2>{t.sessionLibrary}</h2>
          <p className="muted">{t.sessionLibrarySubtitle}</p>
        </div>
        <button className="gold-button" onClick={() => setShowCreate((value) => !value)}>
          <Plus size={16} />
          {t.addSession}
        </button>
      </div>

      <div className="session-library-toolbar">
        <label>
          <Search size={16} />
          <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={t.searchSessions} />
        </label>
        <select value={category} onChange={(event) => setCategory(event.target.value)}>
          {categories.map((item) => <option key={item}>{item}</option>)}
        </select>
      </div>

      {showCreate && (
        <div className="session-create-panel">
          <input value={draft.title} onChange={(event) => setDraft({ ...draft, title: event.target.value })} placeholder={t.sessionTitle} />
          <select value={draft.category} onChange={(event) => setDraft({ ...draft, category: event.target.value })}>
            <option>Individual</option>
            <option>Team Tactical</option>
            <option>Set Pieces</option>
            <option>Technical</option>
            <option>Physical</option>
          </select>
          <select value={draft.level} onChange={(event) => setDraft({ ...draft, level: event.target.value })}>
            <option>Senior</option>
            <option>U18</option>
            <option>U16</option>
            <option>U14</option>
            <option>U12</option>
          </select>
          <input type="number" min="5" step="5" value={draft.duration} onFocus={(event) => { if (event.currentTarget.value === '0') event.currentTarget.value = ''; }} onChange={(event) => setDraft({ ...draft, duration: Number(event.target.value) })} />
          <textarea value={draft.objective} onChange={(event) => setDraft({ ...draft, objective: event.target.value })} placeholder={t.objective} />
          <input value={draft.sourceUrl} onChange={(event) => setDraft({ ...draft, sourceUrl: event.target.value })} placeholder={t.optionalLink} />
          <button className="gold-button" onClick={createSession}>
            <BookmarkPlus size={16} />
            {t.saveSession}
          </button>
        </div>
      )}

      <div className="session-library-grid">
        {filtered.map((item) => (
          <article key={item.id} className="session-library-card">
            <header>
              <span>{item.category}</span>
              <button className={item.favourite ? 'favourite' : ''} onClick={() => toggleFavourite(item.id)} aria-label={t.favourite}>
                <Star size={15} fill={item.favourite ? 'currentColor' : 'none'} />
              </button>
            </header>
            <h3>{item.title}</h3>
            <p>{item.objective || t.noCoachNotes}</p>
            <div className="session-library-meta">
              <span>{item.level}</span>
              <strong>{item.duration} min</strong>
            </div>
            <footer>
              {item.sourceUrl ? (
                <a href={item.sourceUrl} target="_blank" rel="noreferrer">
                  <ExternalLink size={15} />
                  {t.openReference}
                </a>
              ) : (
                <span><BookOpenCheck size={15} /> {t.internalSession}</span>
              )}
              <button onClick={() => removeSession(item.id)} aria-label={t.close}>
                <Trash2 size={14} />
              </button>
            </footer>
          </article>
        ))}
      </div>
    </section>
  );
}
